import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Checkbox } from '@repo/videa-components/src/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

import { useLocalizedFormatDate } from '@/modules/claims/freeClaims/state';

import type { DateFilterState } from '../../../hooks/useImageSelectionModalState';

type VisitDropdownProps = {
  filterState: Record<string, DateFilterState>;
  allDates: string[];
  visibleDates: string[];
  datePresentCount: (date: string) => number;
  isDateChecked: (dateStr: string) => boolean;
  onToggleDate: (dateStr: string) => void;
};

export const VisitDropdown = ({
  filterState,
  allDates,
  visibleDates,
  datePresentCount,
  isDateChecked,
  onToggleDate,
}: VisitDropdownProps) => {
  const localizedFormat = useLocalizedFormatDate();
  const formatDate = (dateStr: string) =>
    localizedFormat(dateStr, false, 'MMM d, yyyy');

  const triggerLabel =
    visibleDates.length === allDates.length ? (
      <Trans>Visits: All</Trans>
    ) : (
      <Trans>Visits: {visibleDates.length} of {allDates.length}</Trans>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="vai-min-w-[180px] vai-justify-between vai-text-[#18181B] vai-font-normal vai-text-left vai-border-slate-200"
          variant="outline"
        >
          <span className="vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap vai-max-w-[85%]">
            {triggerLabel}
          </span>
          <ChevronDown className="vai-h-4 vai-w-4 vai-shrink-0 vai-opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="vai-w-[--radix-popper-anchor-width] vai-overflow-y-auto vai-max-h-[50vh] vai-flex vai-flex-col vai-gap-1 vai-p-2"
      >
        {allDates.length === 0 ? (
          <DropdownMenuLabel>
            <Trans>No visits found</Trans>
          </DropdownMenuLabel>
        ) : (
          Object.keys(filterState)
            .sort((dateA, dateB) => dateB.localeCompare(dateA))
            .map((dateStr) => {
              const formattedDate = formatDate(dateStr);
              const presentCount = datePresentCount(dateStr);
              const checked = isDateChecked(dateStr);
              return (
                <DropdownMenuItem
                  key={`visit-${dateStr}`}
                  onSelect={(e) => e.preventDefault()}
                  className="vai-flex vai-items-start vai-gap-2 vai-cursor-pointer"
                  onClick={() => onToggleDate(dateStr)}
                >
                  <Checkbox checked={checked} className="vai-mt-0.5" />
                  <div className="vai-flex vai-flex-col vai-gap-0.5 vai-w-full vai-leading-tight">
                    <span className="vai-font-medium vai-text-black vai-text-left vai-w-full">
                      {formattedDate}
                    </span>
                    <span className="vai-text-xs vai-text-gray-500 vai-text-left vai-w-full">
                      <Trans>{presentCount} images</Trans>
                    </span>
                  </div>
                </DropdownMenuItem>
              );
            })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
